<template>
	<div>
		<b-nav vertical left class="tool-bar left">
			<li
				v-for="(tool, index) in visualizationTools"
				:key="index"
				:data-tool="tool.key || tool.icon"
				:title="tool.displayName"
				:class="{'section-starter': tool.sectionStarter}"
				class="tool-bar-item"
				@click="addTool(tool)"
			>
				<i :class="'tan-icon-' + tool.icon" />
				<div v-if="tool.description" class="tool-info">
					<h4
						v-if="!!tool.title"
						:class="{
							'mb-0': tool.shortcut
						}"
					>
						{{ tool.title }}
					</h4>
					<p v-if="tool.shortcut">
						({{ tool.shortcut }})
					</p>
				</div>
				<ul v-if="tool.submenu" class="tool-menu list-unstyled">
					<li
						v-for="subtool in tool.submenu"
						:key="subtool.key"
						class="tool-menu-item"
						:data-tool="subtool.key"
					>
						{{ subtool.label }}
					</li>
				</ul>
			</li>
		</b-nav>
		<portal to="smartpanel-slim-left-1">
			<element-manager
				v-if="scenario"
				v-show="elementManagerIsOpen"
				panel="slim1"
				display-name="Element Manager"
				:scenario="scenario"
				:expanded="panels.slim1.expandedIndex === 0"
				:searchable="true"
				:expandable="false"
				:theoretically-resizable="false"
				:currently-resizable="resizable"
				@remove="removeTool('slim1', elementManagerIndex)"
				@expand="expandTool('slim1', elementManagerIndex)"
				v-on="$listeners"
			/>
			<component
				:is="tool.componentName"
				v-for="(tool, index) in panel1Tools"
				v-show="panels.slim1.expandedIndex === null || panels.slim1.expandedIndex === index"
				:key="index"
				panel="slim1"
				:scenario="scenario"
				:expanded="panels.slim1.expandedIndex === index"
				:display-name="tool.displayName"
				:searchable="tool.searchable"
				:expandable="tool.expandable"
				@remove="removeTool('slim1', index)"
				@expand="expandTool('slim1', index)"
				v-on="$listeners"
			/>
		</portal>
		<portal v-if="showPanel2" to="smartpanel-slim-left-2">
			<component
				:is="tool.componentName"
				v-for="(tool, index) in panels.slim2.tools"
				v-show="panels.slim2.expandedIndex === null || panels.slim2.expandedIndex === index"
				:key="index"
				panel="slim2"
				:scenario="scenario"
				:expanded="panels.slim2.expandedIndex === index"
				:display-name="tool.displayName"
				:searchable="tool.searchable"
				:expandable="tool.expandable"
				@remove="removeTool('slim2', index)"
				@expand="expandTool('slim2', index)"
				v-on="$listeners"
			/>
		</portal>
	</div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import ElementManager from './Tool/ElementManager.vue';

const area = 'visualizationTools';
const cloneDeep = require('lodash/cloneDeep');

export default {
	name: 'VisualizationTools',
	components: {
		ElementManager,
	},
	props: {
		scenario: {
			type: Object,
			required: false,
			default: () => {
				return {};
			},
		},
	},
	data() {
		return {
			visualizationTools: [
				{
					icon: 'home',
				},
				{
					componentName: ElementManager.name,
					icon: 'project-management',
					panel: 'slim1',
					resizable: false,
					sectionStarter: true,
				},
				{
					componentName: 'TourLibrary',
					icon: 'curate',
					panel: 'slim1',
					resizable: true,
				},
				{
					componentName: 'TourStopManager',
					icon: 'map',
					panel: 'slim1',
					resizable: true,
				},
				{
					icon: 'sunlight',
				},
			],
		};
	},
	computed: {
		...mapState(['openPanels']),
		elementManagerIndex() {
			return this.panels.slim1.tools.findIndex((tool) => tool.componentName === ElementManager.name);
		},
		elementManagerIsOpen() {
			return this.elementManagerIndex > -1;
		},
		panel1Tools() {
			const tools = cloneDeep(this.panels.slim1.tools);
			if (this.elementManagerIsOpen) tools.splice(this.elementManagerIndex, 1);
			return tools;
		},
		panels() {
			return this.openPanels.visualizationTools;
		},
		/**
		 * Determines if the Element Manager is resizable, but could be used for other tools as well
		 */
		resizable() {
			return this.panels.slim1.tools.length > 1;
		},
		showPanel2() {
			return this.panels.slim2.tools.length > 0;
		},
	},
	methods: {
		...mapMutations(['closePanel', 'expandPanel', 'openPanel']),
		addTool(tool) {
			if (tool.icon === 'home') {
				this.$root.$emit('asset-data-edit');
			}
			if (
				tool.componentName &&
				tool.panel &&
				!(tool.componentName === ElementManager.name && this.elementManagerIsOpen)
			) {
				this.openPanel({
					area,
					panel: {
						componentName: tool.componentName,
						displayName: tool.displayName,
						searchable: tool.searchable === true,
						expandable: tool.expandable === true,
					},
					smartPanel: tool.panel,
				});
				this.checkExpandElementManager(tool.panel);
			}
		},
		checkExpandElementManager(panel) {
			if (
				this.panels[panel].tools.length === 1 &&
				this.panels[panel].tools[0].componentName === ElementManager.name
			) {
				this.panels[panel].expandedIndex = 0;
			}
		},
		expandTool(panel, index) {
			this.expandPanel({ area, index, smartPanel: panel });
		},
		removeTool(panel, index) {
			this.closePanel({ area, index, smartPanel: panel });
			this.checkExpandElementManager(panel);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@/assets/variables.scss';
ul {
	margin: 0;
}
.tool-bar.right .tool-info {
	right: calc(100% + 1px);
}
.tool-bar.left .tool-info {
	left: calc(100% + 1px);
}
.tool-info {
	white-space: initial;
	position: absolute;
	top: 0px;
	background: $body-bg-dark;
	padding: 0.8rem;
	width: 14rem;
	font-size: 0.7rem;
	z-index: 99999 !important;
	cursor: default;
	h4 {
		font-size: 0.75rem;
	}
	a {
		cursor: pointer;
		text-decoration: underline;
		font-weight: bold;
	}
}
.tool-bar {
	user-select: none;
	white-space: nowrap;
	margin: 0;
	&.left {
		border-right: 1px solid $body-color;
	}
	&.right {
		border-left: 1px solid $body-color;
	}
	& > hr {
		margin: 0.5rem 0;
		background: $body-color;
	}
	& > .tool-bar-item {
		position: relative;
		cursor: pointer;
		font-family: $font-family-medium;
		font-size: 1.1rem;
		padding: 0.2rem 0.2rem calc(0.2rem - 2px) 0.2rem;
		border-bottom: 1px solid $body-color;

		.tool-info,
		.tool-menu {
			display: none;
		}
		&:hover {
			background-color: $body-bg;
			.tool-info {
				display: block;
			}
		}
		&.in {
			background-color: $body-bg;
			.tool-menu {
				display: block;
			}
			.tool-info {
				display: none;
			}
		}
		&.section-starter {
			border-top: 1px solid $body-color;
			margin-top: 0.5rem;
		}
	}
}
.tool-menu {
	font-family: $font-family-sans-serif;
	font-size: $dropdown-font-size;
	position: absolute;
	top: 0px;
	background: $dropdown-bg;
	width: 14rem;
	padding: $dropdown-padding-y 0;
	& > .tool-menu-item {
		&:not(:last-child) {
			border-bottom: 1px solid $gray-500;
		}
		padding: $dropdown-item-padding-y $dropdown-item-padding-x;
		&:hover {
			color: $dropdown-link-hover-color;
			background: $dropdown-link-hover-bg;
		}
	}
}
li.tool-bar-item {
	font-size: 1.1rem;
	padding: 0.2rem 0.2rem calc(0.2rem - 2px) 0.2rem;
	border-bottom: 1px solid $body-color;
}

/* width */
::-webkit-scrollbar {
	width: 5px;
}

/* Track */
::-webkit-scrollbar-track {
	background: #f1f1f1;
}

/* Handle */
::-webkit-scrollbar-thumb {
	background: #888;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
	background: #555;
}
</style>
